import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const d = Object.fromEntries(new FormData(e.currentTarget).entries());
    if (d.password !== d.confirm) {
      setError("Both passwords must match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: d.password });
      setDone(true);
      toast.success("Password updated. Log in with your new password.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 pb-24 pt-32" data-testid="reset-password-page">
      <Reveal>
        <div className="text-center">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Almost there</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-forest">NEW PASSWORD.</h1>
        </div>
        <div className="mt-8 rounded-3xl border border-sand bg-white p-6 sm:p-8">
          {!token ? (
            <p className="text-center text-sm text-ink/60">This link is missing its token. Request a fresh one from the log-in screen.</p>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <p className="font-display text-lg font-extrabold text-forest">Done. You're in.</p>
              <p className="mt-2 text-sm text-ink/60">Your new password is active on all devices.</p>
              <Link to="/account" data-testid="reset-login-link" className="mt-5 inline-block rounded-full bg-leaf px-6 py-3 text-sm font-extrabold uppercase tracking-wide text-white hover:bg-forest">Log in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">New password</span>
                <input data-testid="reset-password-input" name="password" type="password" required minLength={8} placeholder="Min 8 characters" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Confirm it</span>
                <input data-testid="reset-confirm-input" name="confirm" type="password" required minLength={8} placeholder="Same again" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold focus:border-leaf focus:bg-white" />
              </label>
              {error && <p data-testid="reset-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-xs font-bold text-red-600">{error}</p>}
              <button data-testid="reset-submit" disabled={loading} className="w-full rounded-full bg-leaf py-3.5 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-forest active:scale-[0.98] disabled:opacity-60">
                {loading ? "Saving..." : "Save new password"}
              </button>
            </form>
          )}
        </div>
      </Reveal>
    </div>
  );
}
